/*
 * Helpers for the signup wizard that don't belong in wizardTypes but are used
 * by multiple steps. Pricing logic (buildPlan) has moved server-side.
 */
import { CARD_LIMIT, fmtMoney } from "./rackRates";

export type Answers = {
  needs: string[];
  volumes: Record<string, string>;
  email?: string;
};

/*
 * The registry-access gate. Both countries gate access behind credentials and
 * both then run a 31-day clock — the part buyers most underestimate.
 *
 * US:     a Subscription ACCOUNT Number (SAN), priced per area code, renewed
 *         annually, first five area codes free. 16 CFR 310 / FTC TSR fee rule.
 * Canada: note the acronym collision — Canada's SAN is a Subscription ACCESS
 *         Number. Registration is free and yields a RAN (Registration Access
 *         Number, which doubles as the user ID); a paid subscription is then
 *         required to download (per area code) or query ($0.50 per lookup, up to
 *         100 numbers a session). CRTC / lnnte-dncl.gc.ca.
 * Clock:  US — safe harbor requires the registry was accessed no more than 31
 *         days before the call. Canada — may not call a number registered on the
 *         DNCL more than 31 days.
 *
 * No claim that we hold or renew the customer's subscriptions: we don't.
 */
type RegistryGateRow = { bold: string; text: string };
type RegistryGateEntry = { title: string; rows: RegistryGateRow[] };

const REGISTRY_GATE: Record<string, RegistryGateEntry> = {
  scrub: {
    title: "What it takes to scrub legally",
    rows: [
      { bold: "United States", text: " \u2014 federal DNC access requires a SAN, a Subscription Account Number, priced per area code and renewed every year." },
      { bold: "Canada", text: " \u2014 the National DNCL is two steps: a RAN (Registration Access Number) to register, free, then a paid subscription with its own Subscription Access Number before you may download or query. Download is priced per area code; query is priced per lookup." },
      { bold: "Both run a 31-day clock", text: " \u2014 in the US your safe harbor only holds if the registry was accessed within 31 days of the call. Canada bars calling any number that has been on the DNCL more than 31 days." },
    ],
  },
};

export const registryGateFor = (key: string) => REGISTRY_GATE[key] ?? null;

/**
 * "ops@acme.com" -> "Acme", "dev@ibm.com" -> "IBM". Empty when there's nothing
 * usable — webmail, or one of our own domains (which only appears in testing).
 */
export function companyFromEmail(email = ""): string {
  const domain = email.split("@")[1];
  if (!domain) return "";
  const skip = [
    "gmail", "yahoo", "outlook", "hotmail", "icloud", "aol", "proton", "protonmail",
    "dnc", "sonera", "purecallerid", "dncscrub",
  ];
  const name = domain.split(".")[0];
  if (!name || skip.includes(name.toLowerCase())) return "";
  // Short domains are nearly always initialisms (ibm, att, hp); title-casing
  // those gives "Ibm", which reads worse than no personalization.
  if (name.length <= 3) return name.toUpperCase();
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export const NEED_LABELS: Record<string, string> = {
  dncscrub: "Serial litigators & DNC",
  reassigned: "Reassigned numbers",
  trustcall: "Flagged as spam",
  complianceguide: "State & campaign rules",
};

export { fmtMoney, CARD_LIMIT };
